import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FC,
} from 'react';
import Link from 'next/link';
import { useInView } from 'framer-motion';
import { createPortal } from 'react-dom';

import {
  HERO_START_DELAY_MS,
  MASCOT_HREF,
  type MascotStep,
} from '#/lib/mascot';
import { cn } from '#/lib/utils';

import { BlobSvg, type BlobGaze } from './BlobSvg';

import './blob-mascot.css';

type Props = {
  step: MascotStep;
};

/**
 * Edge anchor only — peek amount lives in transform %.
 *
 *   bottom: hidden  100% → peek  20%  (~80% showing)
 *   right:  hidden  100% → peek  20%
 *   left:   hidden -100% → peek -20%
 */
function anchorClass(step: MascotStep): string {
  switch (step.anchor) {
    case 'bottom-right':
      // Lifted off the hero's bottom edge so it sits nearer the CTA row.
      return 'absolute bottom-[15%] right-0 z-20';
    case 'bottom-left':
      return 'absolute bottom-[15%] left-0 z-20';
    case 'bottom-center':
      // Pinned to the browser's bottom edge (portalled to <body>) so it stays
      // put while scrolling. Blob is 135px wide; can't use -translate-x-1/2
      // because the peek keyframes own `transform`.
      return 'fixed bottom-0 left-[calc(50%-67.5px*var(--blob-scale))] z-20';
    case 'bottom':
      return 'absolute bottom-0 left-0 z-20';
    case 'section-left':
      return 'absolute left-0 top-1/2 z-20';
    case 'section-right':
      return 'absolute right-0 top-1/2 z-20';
    case 'section-first-card':
      // Rendered as a sibling *under* the host card (card is z-[1]). Base
      // sits on the card's top edge, toward the right, so the eyes-only peek
      // (100% → 35%) rises from tucked behind the card to just clearing its
      // top edge.
      return 'absolute z-0 right-32 bottom-full';
    case 'card-left':
      // Under the host card, just inside its left edge (4px, so the breathe
      // scale can't poke out while hidden); the peek slides it out past that
      // edge (0% → -62%) so the eyes clear the card.
      return 'absolute z-0 left-4 top-1/2';
    case 'card-right':
      return 'absolute z-0 right-4 top-1/2';
    default:
      return '';
  }
}

/** Direction of travel for the peek — drives both the keyframes and the gaze. */
function peekDirection(step: MascotStep): 'up' | 'left' | 'right' {
  if (step.motion === 'peek-top') return 'up';
  if (step.motion === 'peek-left') return 'left';
  if (step.motion === 'peek-right') return 'right';
  // peer
  if (step.anchor === 'section-left') return 'right';
  if (step.anchor === 'section-right') return 'left';
  if (step.anchor === 'card-left') return 'left';
  if (step.anchor === 'card-right') return 'right';
  return 'up';
}

const PEEK_CLASS: Record<ReturnType<typeof peekDirection>, string> = {
  up: 'blob-peek-top',
  left: 'blob-peek-left',
  right: 'blob-peek-right',
};

/** Peeks that start fully tucked under a card and slide out of one edge. */
const CARD_EDGE_PEEK_CLASS: Partial<Record<MascotStep['anchor'], string>> = {
  'section-first-card': 'blob-peek-top-eyes',
  'card-left': 'blob-peek-out-left',
  'card-right': 'blob-peek-out-right',
};

/** Scroll distance (px) after which a viewport-pinned peek slides away. */
const PINNED_DISMISS_SCROLL_PX = 300;

/** The blink cycle's second blink (45.5% of 9s) — the one we align to. */
const BLINK_CYCLE_ANCHOR_MS = 4095;
/** How long after a step starts we want its first visible blink. */
const FIRST_BLINK_AFTER_START_MS = 1200;
/** Bounce: blink as it taps the CTA button (apex is 1.88s in). */
const BOUNCE_BLINK_AFTER_START_MS = 1880;

/** Negative delay shifts the blink cycle so a blink lands soon after entry. */
function blinkDelayStyle(
  step: MascotStep,
  firstBlinkAfterMs = FIRST_BLINK_AFTER_START_MS,
): CSSProperties {
  return {
    ['--blob-blink-delay' as string]: `${
      step.delayMs + firstBlinkAfterMs - BLINK_CYCLE_ANCHOR_MS
    }ms`,
  } as CSSProperties;
}

/**
 * Bounce: hops along the floor, one bigger mid-run jump, hops off.
 *
 * Default (heroes): apex taps the primary CTA. Measured from the first
 * non-self link in the host; sits behind copy (z-0) so it pops up under
 * the button rather than covering it.
 *
 * `data-mascot-bounce="cards"` (Card Grid): no CTA — apex taps the bottom
 * of the middle card. Painted above the cards (z-10) so the ~120px blob
 * isn't swallowed by the card layer in the slim floor lane; drops the
 * hero intro delay since mid-page already waited on viewport entry.
 */
const BounceLink: FC<{ step: MascotStep }> = ({ step }) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const [apex, setApex] = useState<{ h: number; x: number } | null>(null);
  const [onCards, setOnCards] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    const host = el?.offsetParent as HTMLElement | null;
    if (!el || !host) return;

    const hr = host.getBoundingClientRect();
    const cardsMode = host.dataset.mascotBounce === 'cards';
    setOnCards(cardsMode);

    if (cardsMode) {
      const grid = host.querySelector<HTMLElement>(
        '[data-mascot-bounce-target]',
      );
      const cards = grid
        ? Array.from(grid.children).filter(
            (node): node is HTMLElement => node instanceof HTMLElement,
          )
        : [];
      const mid = cards[Math.floor((cards.length - 1) / 2)] ?? cards[0] ?? grid;
      if (!mid) return;
      const br = mid.getBoundingClientRect();
      // Floor → underside of the middle card (keep a visible tap even if tight).
      setApex({
        h: Math.max(48, Math.round(hr.bottom - br.bottom)),
        x: Math.round(br.left + br.width / 2 - hr.left),
      });
      return;
    }

    // First real link in the hero that isn't us: the primary button.
    const button = Array.from(
      host.querySelectorAll<HTMLElement>('a[href]'),
    ).find((a) => a !== el && !el.contains(a));
    if (!button) return;
    const br = button.getBoundingClientRect();
    setApex({
      h: Math.round(hr.bottom - br.top),
      x: Math.round(br.left + br.width / 2 - hr.left),
    });
  }, []);

  // Card grids already gate on inView; strip the hero page-load hold.
  const delayMs = onCards
    ? Math.max(200, step.delayMs - HERO_START_DELAY_MS)
    : step.delayMs;

  // Layout pass so cards-mode delay/z-index land before the animation clock starts.
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    // Re-measure just before the run starts (hero copy / cards have settled).
    const t = window.setTimeout(measure, Math.max(0, delayMs - 100));
    window.addEventListener('resize', measure);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, [measure, delayMs]);

  return (
    <Link
      ref={ref}
      href={MASCOT_HREF}
      aria-label="Learn about Rails"
      className={cn(
        'blob-bounce-host pointer-events-auto absolute bottom-0 left-0',
        // Cards: above the row so the floor run stays visible. Heroes: under copy.
        onCards ? 'z-10' : 'z-0',
      )}
      style={
        {
          ...blinkDelayStyle({ ...step, delayMs }, BOUNCE_BLINK_AFTER_START_MS),
          // x travel, y hops and the contact shadow share one clock.
          '--blob-bounce-ms': `${step.durationMs}ms`,
          '--blob-bounce-delay': `${delayMs}ms`,
          ...(apex
            ? { '--blob-cta-h': `${apex.h}px`, '--blob-cta-x': `${apex.x}px` }
            : null),
        } as CSSProperties
      }
    >
      <span className="blob-motion-bounce-x relative block h-[calc(120px*var(--blob-scale))] w-[calc(135px*var(--blob-scale))]">
        <span aria-hidden="true" className="blob-bounce-shadow" />
        <span className="blob-motion-bounce-y relative block h-full w-full">
          {/* Board bounce keeps the gaze centred; squash comes from bounce-y. */}
          <BlobSvg color={step.color} blink />
        </span>
      </span>
    </Link>
  );
};

const RollLink: FC<{ step: MascotStep; className: string }> = ({
  step,
  className,
}) => (
  <Link
    href={MASCOT_HREF}
    aria-label="Learn about Rails"
    className={cn(
      'pointer-events-auto h-[calc(135px*var(--blob-scale))] w-[calc(135px*var(--blob-scale))]',
      className,
    )}
    style={blinkDelayStyle(step)}
  >
    {/*
     * One rigid unit: body and eyes rotate together like a face printed
     * on a ball. Translate, spin and landing squash share one transform.
     */}
    <span
      className="blob-motion-roll block h-full w-full"
      style={{
        animationDelay: `${step.delayMs}ms`,
        animationDuration: `${step.durationMs}ms`,
      }}
    >
      <BlobSvg color={step.color} round blink />
    </span>
  </Link>
);

/**
 * Clip box for the roll: full section width, extended 60px below the hero so
 * the floor sits across the seam into the next section, while the 110vw exit
 * stays clipped and can't cause horizontal overflow.
 *
 * The roll itself starts only once its *floor* (bottom blob-height of the
 * box) is on screen — the hero can be well in view on short viewports while
 * the floor is still below the fold.
 */
const RollClip: FC<{ step: MascotStep }> = ({ step }) => {
  const floorRef = useRef<HTMLSpanElement>(null);
  const floorInView = useInView(floorRef, { amount: 0.6, once: true });
  return (
    <span className="pointer-events-none absolute inset-x-0 top-0 -bottom-[calc(60px*var(--blob-scale))] z-20 overflow-hidden">
      <span
        ref={floorRef}
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-[calc(135px*var(--blob-scale))]"
      />
      {floorInView ? (
        <RollLink step={step} className="absolute bottom-0 left-0" />
      ) : null}
    </span>
  );
};

export const BlobMascotStep: FC<Props> = ({ step }) => {
  const animStyle = {
    animationDelay: `${step.delayMs}ms`,
    animationDuration: `${step.durationMs}ms`,
    ...blinkDelayStyle(step),
  } as CSSProperties;

  // One-shot: once the peek has run, drop the (now invisible) link entirely
  // so nothing lingers at the edge or stays in the tab order.
  const [done, setDone] = useState(false);

  // The roll escapes the hero's `overflow-hidden` by portalling into the
  // enclosing page <section> (see PageSection: `relative`, no clip).
  // `undefined` = not probed yet; `null` = no section host, render in place.
  const [rollHost, setRollHost] = useState<HTMLElement | null | undefined>();
  const rollProbeRef = useCallback((el: HTMLSpanElement | null) => {
    if (el) setRollHost(el.closest<HTMLElement>('section[data-section]'));
  }, []);

  // Viewport-pinned steps render into <body> so `fixed` isn't trapped by the
  // hero's stacking context / any transformed ancestor.
  const isViewportPinned = step.anchor === 'bottom-center';
  const [body, setBody] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (isViewportPinned) setBody(document.body);
  }, [isViewportPinned]);

  // Pinned peek slides away as soon as the visitor scrolls, so it never sits
  // over the content below the hero.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!isViewportPinned || dismissed) return;
    const startY = window.scrollY;
    const onScroll = () => {
      if (Math.abs(window.scrollY - startY) > PINNED_DISMISS_SCROLL_PX) {
        setDismissed(true);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isViewportPinned, dismissed]);

  if (step.motion === 'bounce') {
    return <BounceLink step={step} />;
  }

  if (step.motion === 'roll') {
    return (
      <>
        {/* Probe: locates the enclosing page <section> (relative, unclipped). */}
        <span ref={rollProbeRef} aria-hidden="true" className="hidden" />
        {rollHost === undefined ? null : rollHost ? (
          createPortal(<RollClip step={step} />, rollHost)
        ) : (
          <RollLink step={step} className={anchorClass(step)} />
        )}
      </>
    );
  }

  const direction = peekDirection(step);
  const gaze: BlobGaze = direction;
  const yCenter =
    step.anchor === 'section-left' ||
    step.anchor === 'section-right' ||
    step.anchor === 'card-left' ||
    step.anchor === 'card-right';

  if (done) return null;

  const link = (
    <Link
      href={MASCOT_HREF}
      aria-label="Learn about Rails"
      className={cn(
        'blob-mini pointer-events-auto',
        yCenter || isViewportPinned ? 'relative' : anchorClass(step),
        CARD_EDGE_PEEK_CLASS[step.anchor] ?? PEEK_CLASS[direction],
      )}
      style={animStyle}
      onAnimationEnd={(event) => {
        // Only the peek itself (on this element) ends; breathe/blink loop.
        if (event.target === event.currentTarget) setDone(true);
      }}
    >
      <span className="blob-breathe block h-full w-full">
        <BlobSvg color={step.color} blink gaze={gaze} />
      </span>
    </Link>
  );

  if (yCenter) {
    return (
      <div
        className={cn('pointer-events-none', anchorClass(step))}
        style={{ transform: 'translateY(-50%)' }}
      >
        {link}
      </div>
    );
  }

  if (isViewportPinned) {
    if (!body) return null;
    return createPortal(
      <span
        className={cn(
          'blob-pinned-host block',
          anchorClass(step),
          dismissed && 'blob-peek-dismiss',
        )}
        onTransitionEnd={(event) => {
          if (event.target === event.currentTarget) setDone(true);
        }}
      >
        {link}
      </span>,
      body,
    );
  }

  return link;
};
